import React, { useState, useMemo, useRef } from "react";
// import { Plus, Trash2, RotateCcw } from "lucide-react";
import { FaPlus, FaTrash, FaRedo } from "react-icons/fa";

/* =========================================================================
   IDENTIDADE VISUAL
   Cores exatas da paleta de marca — aplicadas via style inline porque o
   Tailwind deste ambiente não compila classes arbitrárias (bg-[#EC6643]).
   ========================================================================= */
const C = {
  // Fundos
  bg: "#F6F6F6",
  surface: "#FFFFFF",
  surfaceSoft: "#F1F2F3",
  surfaceWarm: "#FEF8F6",

  // Bordas
  border: "#E6EBEF",
  borderStrong: "#D4D6DB",

  // Textos
  textDark: "#1C2D38",
  textBody: "#40444C",
  textMuted: "#6A717E",
  textFaint: "#C0C3C9",

  // Laranja principal
  accent: "#EC6643",
  accentDark: "#D75D3D",
  accentLight: "#F29881",
  accentSoft: "#FEF8F6",
  accentSoftBorder: "#F6B9A9",

  // Azul
  blueDark: "#1C2D38",
  blueMid: "#295074",
  blueLight: "#3D607A",
  blueSoft: "rgba(41, 80, 116, 0.12)",

  // Dourado
  gold: "#B57600",
  goldLight: "#F8C25A",
  goldSoft: "#FEF7EA",

  // Estados
  success: "#588157",
  danger: "#D75D3D",

  // Barras e trilhas
  track: "#F1F2F3",
};

/* =========================================================================
   CATÁLOGO DE TIPOS DE INVESTIMENTO
   ========================================================================= */
const TIPOS = {
  cdb_cdi: { label: "CDB — pós-fixado (% do CDI)", indexador: "CDI", isento: false, custodia: false, defaultTaxa: 100 },
  cdb_pre: { label: "CDB — prefixado", indexador: "PRE", isento: false, custodia: false, defaultTaxa: 14 },
  cdb_ipca: { label: "CDB — IPCA+", indexador: "IPCA", isento: false, custodia: false, defaultTaxa: 7 },
  lci_cdi: { label: "LCI — pós-fixado (% do CDI)", indexador: "CDI", isento: true, custodia: false, defaultTaxa: 90 },
  lci_pre: { label: "LCI — prefixado", indexador: "PRE", isento: true, custodia: false, defaultTaxa: 11 },
  lci_ipca: { label: "LCI — IPCA+", indexador: "IPCA", isento: true, custodia: false, defaultTaxa: 6 },
  lca_cdi: { label: "LCA — pós-fixado (% do CDI)", indexador: "CDI", isento: true, custodia: false, defaultTaxa: 90 },
  lca_pre: { label: "LCA — prefixado", indexador: "PRE", isento: true, custodia: false, defaultTaxa: 11 },
  lca_ipca: { label: "LCA — IPCA+", indexador: "IPCA", isento: true, custodia: false, defaultTaxa: 6 },
  tesouro_selic: { label: "Tesouro Selic", indexador: "SELIC", isento: false, custodia: true, isencao10k: true, defaultTaxa: 100 },
  tesouro_reserva: { label: "Tesouro Reserva (100% Selic, sem marcação a mercado)", indexador: "SELIC", isento: false, custodia: true, isencao10k: true, taxaFixa: 100 },
  tesouro_pre: { label: "Tesouro Prefixado", indexador: "PRE", isento: false, custodia: true, defaultTaxa: 14 },
  tesouro_ipca: { label: "Tesouro IPCA+", indexador: "IPCA", isento: false, custodia: true, defaultTaxa: 8 },
  poupanca: { label: "Poupança", indexador: "POUP", isento: true, custodia: false, defaultTaxa: 0 },
  outro_cdi: { label: "Outro — pós-fixado (% do CDI)", indexador: "CDI", isento: false, custodia: false, defaultTaxa: 100 },
  outro_isento: { label: "Outro — isento (CRI/CRA/Deb. incentivada) prefixado", indexador: "PRE", isento: true, custodia: false, defaultTaxa: 11 },
};

/* =========================================================================
   TABELAS FISCAIS
   ========================================================================= */
const IOF_REGRESSIVO = [96, 93, 90, 86, 83, 80, 76, 73, 70, 66, 63, 60, 56, 53, 50, 46, 43, 40, 36, 33, 30, 26, 23, 20, 16, 13, 10, 6, 3, 0];

function aliquotaIR(dias) {
  if (dias <= 180) return 0.225;
  if (dias <= 360) return 0.2;
  if (dias <= 720) return 0.175;
  return 0.15;
}
function faixaIRTexto(dias) {
  if (dias <= 180) return "22,5% (até 180 dias)";
  if (dias <= 360) return "20% (181 a 360 dias)";
  if (dias <= 720) return "17,5% (361 a 720 dias)";
  return "15% (acima de 720 dias)";
}
function aliquotaIOF(dias) {
  if (dias >= 30) return 0;
  const d = Math.max(1, Math.min(30, Math.round(dias)));
  return IOF_REGRESSIVO[d - 1] / 100;
}

/* =========================================================================
   PROJEÇÕES BOLETIM FOCUS (Banco Central) — atualizado em 11/07/2026.
   Mesmos valores usados como padrão pelo simulador oficial do Tesouro Direto.
   Ficam desatualizados com o tempo: confira o boletim mais recente em
   bcb.gov.br/publicacoes/focus
   ========================================================================= */
const FOCUS_ATUALIZADO_EM = "11/07/2026";
const FOCUS_SELIC = { 2026: 14.0, 2027: 12.0, 2028: 10.5, 2029: 10.0, 2030: 10.0 };
const FOCUS_IPCA = { 2026: 5.33, 2027: 4.17, 2028: 3.7, 2029: 3.5 };

function taxaFocusAno(mapa, ano) {
  if (mapa[ano] !== undefined) return mapa[ano];
  const anos = Object.keys(mapa).map(Number);
  const anoMax = Math.max(...anos), anoMin = Math.min(...anos);
  if (ano < anoMin) return mapa[anoMin];
  return mapa[anoMax];
}
function mediaPonderadaFocus(mapa, dtIni, dtFim) {
  let totalDias = 0, soma = 0;
  const cur = new Date(dtIni);
  while (cur < dtFim) {
    soma += taxaFocusAno(mapa, cur.getFullYear());
    totalDias++;
    cur.setDate(cur.getDate() + 1);
  }
  return totalDias > 0 ? soma / totalDias : taxaFocusAno(mapa, dtIni.getFullYear());
}

/* =========================================================================
   DATAS / DIAS ÚTEIS
   ========================================================================= */
function diasUteisEntre(d1, d2) {
  // aproximação: exclui sábados e domingos; não considera feriados nacionais/locais
  let count = 0;
  const cur = new Date(d1);
  cur.setDate(cur.getDate() + 1);
  while (cur <= d2) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/* =========================================================================
   FORMATAÇÃO
   ========================================================================= */
function fmtBRL(v) {
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function fmtPct(v) {
  return v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "%";
}
function formatMoedaDigitando(valorAtual) {
  let digitos = valorAtual.replace(/\D/g, "");
  digitos = digitos.replace(/^0+(?=\d)/, "");
  if (digitos === "") digitos = "0";
  while (digitos.length < 3) digitos = "0" + digitos;
  const parteInteira = digitos.slice(0, -2);
  const parteCentavos = digitos.slice(-2);
  return parseInt(parteInteira, 10).toLocaleString("pt-BR") + "," + parteCentavos;
}
function parseMoeda(valorFormatado) {
  if (!valorFormatado) return 0;
  const limpo = valorFormatado.replace(/\./g, "").replace(",", ".");
  return parseFloat(limpo) || 0;
}
function toISO(d) {
  return d.toISOString().slice(0, 10);
}

/* =========================================================================
   CÁLCULO FINANCEIRO POR INVESTIMENTO
   ========================================================================= */
function calcInvestimento(inv, params) {
  const cfg = TIPOS[inv.tipo];
  const { valor, dias, diasUteis, baseCalculo, cdi, selic, ipca } = params;
  const diasRend = baseCalculo === "uteis" ? diasUteis : dias;
  const baseAno = baseCalculo === "uteis" ? 252 : 365;
  let taxaAnual = 0;

  if (cfg.indexador === "CDI") {
    taxaAnual = (cdi / 100) * (inv.taxa / 100);
  } else if (cfg.indexador === "SELIC") {
    taxaAnual = (selic / 100) * (inv.taxa / 100);
  } else if (cfg.indexador === "PRE") {
    taxaAnual = inv.taxa / 100;
  } else if (cfg.indexador === "IPCA") {
    taxaAnual = (1 + ipca / 100) * (1 + inv.taxa / 100) - 1;
  } else if (cfg.indexador === "POUP") {
    const mensal = selic > 8.5 ? 0.005 : (0.7 * (selic / 100)) / 12;
    taxaAnual = Math.pow(1 + mensal, 12) - 1;
  }

  // rendimento anualiza pela base escolhida (mercado usa dias úteis/252 por convenção ANBIMA);
  // IR, IOF e custódia sempre em dias corridos, pois é assim que a lei define as faixas
  const montanteBruto = valor * Math.pow(1 + taxaAnual, diasRend / baseAno);
  const rendimentoBruto = montanteBruto - valor;

  const iof = dias < 30 ? rendimentoBruto * aliquotaIOF(dias) : 0;
  const rendAposIOF = rendimentoBruto - iof;

  const ir = cfg.isento ? 0 : Math.max(0, rendAposIOF) * aliquotaIR(dias);

  const baseCustodia = cfg.isencao10k ? Math.max(0, valor - 10000) : valor;
  const custodia = cfg.custodia ? baseCustodia * 0.002 * (dias / 365) : 0;
  const admFee = (inv.admFee || 0) > 0 ? valor * (inv.admFee / 100) * (dias / 365) : 0;

  const liquidoFinal = valor + rendAposIOF - ir - custodia - admFee;
  const rentPeriodo = ((liquidoFinal - valor) / valor) * 100;
  const rentAnualizada = (Math.pow(liquidoFinal / valor, 365 / dias) - 1) * 100;

  const ipcaPeriodo = Math.pow(1 + ipca / 100, dias / 365) - 1;
  const rentRealPeriodo = ((1 + rentPeriodo / 100) / (1 + ipcaPeriodo) - 1) * 100;
  const rentRealAnualizada = ((1 + rentAnualizada / 100) / (1 + ipca / 100) - 1) * 100;

  return {
    rendimentoBruto, iof, ir, custos: custodia + admFee, liquidoFinal,
    rentPeriodo, rentAnualizada, rentRealPeriodo, rentRealAnualizada, isento: cfg.isento,
  };
}

/* =========================================================================
   ESTADO INICIAL
   ========================================================================= */
function investimentosIniciais() {
  return [
    { id: 1, nome: "CDB Banco X", tipo: "cdb_cdi", taxa: 110, admFee: 0 },
    // { id: 2, nome: "LCI Banco Y", tipo: "lci_cdi", taxa: 95, admFee: 0 },
    { id: 2, nome: "Tesouro Selic", tipo: "tesouro_selic", taxa: 100, admFee: 0 },
    { id: 3, nome: "Tesouro Reserva", tipo: "tesouro_reserva", taxa: 100, admFee: 0 },
    { id: 4, nome: "Poupança", tipo: "poupanca", taxa: 0, admFee: 0 },
  ];
}
function datasIniciais() {
  const hoje = new Date();
  const daqui1ano = new Date(hoje);
  daqui1ano.setFullYear(daqui1ano.getFullYear() + 1);
  return { aplicacao: toISO(hoje), vencimento: toISO(daqui1ano) };
}

/* =========================================================================
   SUBCOMPONENTES DE UI
   ========================================================================= */
function Field({ label, children, hint }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: C.textDark, marginBottom: 6 }}>
        {label}
      </label>
      {children}
      {hint && <span style={{ display: "block", fontSize: 11, color: C.textFaint, marginTop: 5, lineHeight: 1.35 }}>{hint}</span>}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  background: C.surface,
  border: `1px solid ${C.borderStrong}`,
  color: C.textDark,
  fontSize: 14,
  fontWeight: 500,
  padding: "10px 11px",
  borderRadius: 7,
  outline: "none",
  fontFamily: "inherit",
};

function InfoBox({ children }) {
  return (
    <div style={{
      width: "100%", background: "#FAFBFC", border: `1px solid ${C.border}`, color: C.textMuted,
      fontSize: 12.5, fontWeight: 500, lineHeight: 1.4, padding: "10px 11px", borderRadius: 7,
      minHeight: 40, display: "flex", alignItems: "center",
    }}>
      {children}
    </div>
  );
}

/* =========================================================================
   COMPONENTE PRINCIPAL
   ========================================================================= */
export default function ComparadorRendaFixa() {
  const nextIdRef = useRef(4);
  const datasIni = useMemo(datasIniciais, []);

  const [investimentos, setInvestimentos] = useState(investimentosIniciais);
  const [valorTexto, setValorTexto] = useState("10.000,00");
  const [dataAplicacao, setDataAplicacao] = useState(datasIni.aplicacao);
  const [dataVencimento, setDataVencimento] = useState(datasIni.vencimento);
  const [taxaCDI, setTaxaCDI] = useState(14.15);
  const [taxaSelic, setTaxaSelic] = useState(14.25);
  const [taxaIPCA, setTaxaIPCA] = useState(4.72);
  const [baseCalculo, setBaseCalculo] = useState("uteis");
  const [focusInfo, setFocusInfo] = useState("");

  const valor = parseMoeda(valorTexto);

  function handleValorChange(e) {
    const cursorNoFinal = e.target.selectionStart === e.target.value.length;
    const novo = formatMoedaDigitando(e.target.value);
    setValorTexto(novo);
    if (cursorNoFinal) {
      requestAnimationFrame(() => {
        e.target.setSelectionRange(novo.length, novo.length);
      });
    }
  }

  function updateInv(id, field, value) {
    setInvestimentos(prev => prev.map(inv => {
      if (inv.id !== id) return inv;
      const atualizado = { ...inv, [field]: field === "nome" || field === "tipo" ? value : parseFloat(value) || 0 };
      if (field === "tipo") {
        const cfg = TIPOS[value];
        atualizado.taxa = cfg.taxaFixa ?? cfg.defaultTaxa ?? atualizado.taxa;
      }
      return atualizado;
    }));
  }
  function addInvestimento() {
    const id = nextIdRef.current++;
    setInvestimentos(prev => [...prev, { id, nome: "Novo investimento", tipo: "cdb_cdi", taxa: 100, admFee: 0 }]);
  }
  function removeInvestimento(id) {
    setInvestimentos(prev => prev.filter(inv => inv.id !== id));
  }

  function preencherProjecaoFocus() {
    const dtApl = new Date(dataAplicacao + "T00:00:00");
    const dtVenc = new Date(dataVencimento + "T00:00:00");
    const selicMedia = mediaPonderadaFocus(FOCUS_SELIC, dtApl, dtVenc);
    const ipcaMedia = mediaPonderadaFocus(FOCUS_IPCA, dtApl, dtVenc);
    const cdiMedia = Math.max(0, selicMedia - 0.1);
    setTaxaSelic(Number(selicMedia.toFixed(2)));
    setTaxaCDI(Number(cdiMedia.toFixed(2)));
    setTaxaIPCA(Number(ipcaMedia.toFixed(2)));
    setFocusInfo(`Boletim Focus/BC de ${FOCUS_ATUALIZADO_EM} (mesma referência do simulador do Tesouro Direto) · média ponderada pelos anos do prazo informado`);
  }

  /* ---------- derivados: prazo, IR aplicável, resultados ---------- */
  const { dias, diasUteis, prazoTexto, irTexto } = useMemo(() => {
    const dtApl = new Date(dataAplicacao + "T00:00:00");
    const dtVenc = new Date(dataVencimento + "T00:00:00");
    let d = Math.round((dtVenc - dtApl) / 86400000);
    if (!isFinite(d) || d < 1) d = 1;
    const du = Math.max(1, diasUteisEntre(dtApl, dtVenc));
    return {
      dias: d,
      diasUteis: du,
      prazoTexto: `${d} dias corridos · ${du} dias úteis`,
      irTexto: faixaIRTexto(d),
    };
  }, [dataAplicacao, dataVencimento]);

  const results = useMemo(() => {
    if (valor <= 0) return [];
    const params = { valor, dias, diasUteis, baseCalculo, cdi: taxaCDI, selic: taxaSelic, ipca: taxaIPCA };
    const calc = investimentos.map(inv => ({ inv, r: calcInvestimento(inv, params) }));
    calc.sort((a, b) => b.r.liquidoFinal - a.r.liquidoFinal);
    return calc;
  }, [investimentos, valor, dias, diasUteis, baseCalculo, taxaCDI, taxaSelic, taxaIPCA]);

  const melhor = results[0];
  const segundo = results[1];
  const diferenca = melhor && segundo ? melhor.r.liquidoFinal - segundo.r.liquidoFinal : null;

  const minVal = results.length ? Math.min(...results.map(r => r.r.liquidoFinal)) : 0;
  const maxVal = results.length ? Math.max(...results.map(r => r.r.liquidoFinal)) : 0;
  const spread = maxVal - minVal;

  /* ---------- render ---------- */
  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "0", fontFamily: "Inter, sans-serif", color: C.textDark }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: "0 0 8px 8px", padding: "36px 30px 12px" }}>

          {/* Header */}
          <header style={{ marginBottom: 24 }}>
            {/* font-size:20px;font-weight:600;margin:0 0 4px; */}
            <h1 style={{ fontWeight: 600, fontSize: "20px", margin: "0 0 4px", color: '#1C2D38'}}>
              Comparador de Renda Fixa
            </h1>
            <p style={{ fontSize: 13.5, lineHeight: 1.5, color: '#32414B', margin: 0, maxWidth: 640 }}>
              Compare CDB, LCI, LCA, Tesouro Direto e Poupança já descontando IR regressivo, IOF e taxas — para o mesmo valor e prazo.
            </p>
          </header>

          {/* Parâmetros */}
          <Card>
            <SectionLabel>Parâmetros do investimento</SectionLabel>
            <Grid cols={5}>
              <Field label="Valor a investir (R$)" hint="Digite só os números — a formatação (R$ 1.000,00) aparece sozinha">
                <input style={inputStyle} type="text" inputMode="numeric" autoComplete="off" value={valorTexto} onChange={handleValorChange} />
              </Field>
              <Field label="Data de aplicação">
                <input style={inputStyle} type="date" value={dataAplicacao} onChange={e => setDataAplicacao(e.target.value)} />
              </Field>
              <Field label="Data de vencimento / resgate">
                <input style={inputStyle} type="date" value={dataVencimento} onChange={e => setDataVencimento(e.target.value)} />
              </Field>
              <Field label="Prazo calculado"><InfoBox>{prazoTexto}</InfoBox></Field>
              <Field label="Alíquota de IR aplicável"><InfoBox>{irTexto}</InfoBox></Field>
            </Grid>

            <div style={{ marginTop: 16 }}>
              <Grid cols={4}>
                <Field label="CDI médio projetado p/ o período (% a.a.)">
                  <input style={inputStyle} type="number" step="0.01" value={taxaCDI} onChange={e => setTaxaCDI(parseFloat(e.target.value) || 0)} />
                </Field>
                <Field label="Selic média projetada p/ o período (% a.a.)">
                  <input style={inputStyle} type="number" step="0.01" value={taxaSelic} onChange={e => setTaxaSelic(parseFloat(e.target.value) || 0)} />
                </Field>
                <Field label="IPCA médio projetado p/ o período (% a.a.)">
                  <input style={inputStyle} type="number" step="0.01" value={taxaIPCA} onChange={e => setTaxaIPCA(parseFloat(e.target.value) || 0)} />
                </Field>
                <Field label="Base de cálculo do rendimento">
                  <select style={inputStyle} value={baseCalculo} onChange={e => setBaseCalculo(e.target.value)}>
                    <option value="uteis">Dias úteis / 252 (padrão de mercado)</option>
                    <option value="corridos">Dias corridos / 365</option>
                  </select>
                </Field>
              </Grid>
            </div>

            <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <button onClick={preencherProjecaoFocus} style={{
                display: "flex", alignItems: "center", gap: 6, background: C.accentSoft,
                border: `1px solid ${C.accentSoftBorder}`, color: C.accentDark, fontSize: 12.5, fontWeight: 700,
                padding: "10px 16px", borderRadius: 7, cursor: "pointer",
              }}>
                <FaRedo size={14} /> Preencher com a média projetada (Boletim Focus/BC)
              </button>
              <span style={{ fontSize: 11.5, color: C.textFaint }}>{focusInfo}</span>
            </div>

            <p style={{ fontSize: 12, lineHeight: 1.55, color: C.textMuted, margin: "18px 0 0", paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
              Para investimentos de vários anos, CDI e Selic não ficam parados no patamar de hoje — por isso os campos acima pedem uma{" "}
              <strong style={{ color: C.textDark }}>média projetada para todo o período</strong>, não a taxa do dia. O botão acima calcula essa
              média automaticamente, ponderando pelos anos que o seu prazo cobre, a partir das projeções do Boletim Focus (Banco Central) — os
              mesmos valores usados como padrão pelo simulador oficial do Tesouro Direto. Você também pode digitar sua própria estimativa.
              Preencha as datas reais do investimento que você pretende fazer, pois é a partir delas que o prazo é calculado para apurar IR, IOF
              e custódia.
            </p>
          </Card>

          {/* Lista de investimentos */}
          <Card>
            <SectionLabel>Investimentos comparados</SectionLabel>
            {investimentos.map(inv => (
              <InvestimentoRow key={inv.id} inv={inv} onUpdate={updateInv} onRemove={removeInvestimento} />
            ))}
            <button onClick={addInvestimento} style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              background: "transparent", border: `1.5px dashed ${C.borderStrong}`,
              fontSize: 13, fontWeight: 700, padding: 13, borderRadius: 9, cursor: "pointer",
              color: '#EC6643',
            }}>
              <FaPlus color="#EC6643" size={15} /> Adicionar investimento
            </button>
          </Card>

          {/* Resumo */}
          <SectionLabel>Resultado da comparação</SectionLabel>
          {valor <= 0 ? (
            <Card><span style={{ fontSize: 12.5, color: C.textFaint }}>Preencha o valor a investir acima para ver a comparação.</span></Card>
          ) : (
            <>
              <Grid cols={4} style={{ marginBottom: 22 }}>
                <ResumoCard label="Melhor investimento" value={melhor?.inv.nome ?? "—"} caption="maior valor líquido no vencimento" highlight />
                <ResumoCard label="Valor líquido no vencimento" value={melhor ? fmtBRL(melhor.r.liquidoFinal) : "—"} caption={`sobre ${fmtBRL(valor)} investidos`} highlight />
                <ResumoCard label="Rentabilidade real anualizada" value={melhor ? fmtPct(melhor.r.rentRealAnualizada) : "—"} caption="já descontando IR, taxas e inflação" highlight />
                <ResumoCard
                  label="Vantagem sobre o 2º colocado"
                  value={diferenca !== null ? fmtBRL(diferenca) : "—"}
                  caption={segundo ? `a mais que "${segundo.inv.nome}"` : "adicione outro investimento p/ comparar"}
                />
              </Grid>

              {/* Tabela */}
              <Card>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
                  <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: '#1C2D38' }}>Resultado líquido por investimento</h2>
                  <span style={{ fontSize: 12, color: C.textMuted, fontWeight: 500 }}>
                    {fmtBRL(valor)} · {dias} dias · {investimentos.length} investimento(s)
                  </span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr>
                        {["Investimento", "Rend. bruto", "IOF", "IR", "Custos", "Líquido final", "Rentab. líq. nominal (período)", "Rentab. real (período)", "Anualizada real"].map((h, i) => (
                          <th key={h} style={{
                            textAlign: i === 0 ? "left" : "right", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase",
                            letterSpacing: "0.03em", color: '#40444C', padding: "0 8px 10px", borderBottom: `1px solid ${C.border}`,
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((res, idx) => (
                        <tr key={res.inv.id} style={idx === 0 ? { background: C.accentSoft } : undefined}>
                          <Td first winner={idx === 0}>
                            {res.inv.nome}
                            {idx === 0 && (
                              <span style={{ display: "inline-block", background: C.accent, color: "#fff", fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, marginLeft: 6 }}>
                                melhor líquido
                              </span>
                            )}
                          </Td>
                          <Td>{fmtBRL(res.r.rendimentoBruto)}</Td>
                          <Td neg={res.r.iof > 0}>{res.r.iof > 0 ? "−" + fmtBRL(res.r.iof) : "—"}</Td>
                          <Td neg={res.r.ir > 0}>{res.r.isento ? "isento" : "−" + fmtBRL(res.r.ir)}</Td>
                          <Td>{res.r.custos > 0 ? "−" + fmtBRL(res.r.custos) : "—"}</Td>
                          <Td bold>{fmtBRL(res.r.liquidoFinal)}</Td>
                          <Td>{fmtPct(res.r.rentPeriodo)}</Td>
                          <Td neg={res.r.rentRealPeriodo < 0}>{fmtPct(res.r.rentRealPeriodo)}</Td>
                          <Td neg={res.r.rentRealAnualizada < 0}>{fmtPct(res.r.rentRealAnualizada)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Ranking */}
                <p style={{ fontSize: 11.5, color: C.textFaint, margin: "16px 0 4px" }}>
                  Ranking pelo valor líquido no vencimento — a barrinha mostra a diferença relativa entre a pior e a melhor opção.
                </p>
                <div style={{ marginTop: 4 }}>
                  {results.map((res, idx) => {
                    const pct = spread > 0 ? 18 + ((res.r.liquidoFinal - minVal) / spread) * 82 : 100;
                    const diferencaVsLider = res.r.liquidoFinal - maxVal;
                    return (
                      <div key={res.inv.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 4px", borderBottom: idx === results.length - 1 ? "none" : `1px solid ${C.border}` }}>
                        <div style={{
                          width: 26, height: 26, flexShrink: 0, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 12, fontWeight: 800, background: idx === 0 ? C.accent : C.track, color: idx === 0 ? "#fff" : C.textMuted,
                        }}>{idx + 1}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: idx === 0 ? C.accentDark : C.textDark, marginBottom: 6 }}>{res.inv.nome}</div>
                          <div style={{ background: C.track, borderRadius: 5, height: 8, overflow: "hidden" }}>
                            <div style={{ height: "100%", borderRadius: 5, width: `${pct}%`, background: idx === 0 ? C.accent : C.blueMid }} />
                          </div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0, minWidth: 150 }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: C.textDark }}>{fmtBRL(res.r.liquidoFinal)}</div>
                          <div style={{ fontSize: 11.5, fontWeight: 600, marginTop: 2, color: idx === 0 ? C.accentDark : C.danger }}>
                            {idx === 0 ? "líder" : `−${fmtBRL(Math.abs(diferencaVsLider))} vs. líder`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </>
          )}

          {/* Rodapé */}
          <p style={{ fontSize: 11.5, lineHeight: 1.6, color: '#7592a8', marginTop: 20, borderTop: `1px solid ${C.border}`, paddingTop: 16, paddingBottom: 20 }}>
            <strong style={{ color: C.textMuted }}>Sobre os cálculos:</strong> o prazo é calculado a partir das datas de aplicação e vencimento
            informadas acima; é esse prazo que define IR, IOF e custódia — por isso, preencha as datas reais do investimento que você pretende
            fazer, para que a comparação entre as opções seja justa (mesmo valor, mesmo prazo). Por padrão, o rendimento é anualizado em{" "}
            <strong style={{ color: C.textMuted }}>dias úteis/252</strong> (convenção ANBIMA, usada de praxe pelo mercado); você pode trocar
            para dias corridos/365 no seletor "Base de cálculo". IR e IOF continuam sempre em dias corridos, como determina a lei — IR segue a
            tabela regressiva (22,5% até 180 dias corridos · 20% até 360 · 17,5% até 720 · 15% acima), com LCI, LCA e Poupança isentos; IOF
            regressivo incide sobre o rendimento em resgates antes de 30 dias corridos. A{" "}
            <strong style={{ color: C.textMuted }}>taxa de custódia B3</strong> (0,20% a.a.) incide sobre títulos do Tesouro; no Tesouro Selic
            e no Tesouro Reserva há isenção para os primeiros R$10.000 investidos, cobrando-se apenas sobre o valor excedente — já no Tesouro
            Prefixado e IPCA+ ela incide sobre o total. A <strong style={{ color: C.textMuted }}>rentabilidade real</strong> desconta a
            inflação (IPCA) projetada para o mesmo período, pela fórmula de Fisher — valores negativos indicam perda de poder de compra mesmo
            com ganho nominal. O Tesouro Reserva é a nova LFT-TD1 do Tesouro Direto: rende 100% da Selic, sem marcação a mercado, com liquidez
            imediata 24×7. Os dias úteis são estimados excluindo apenas fins de semana, sem considerar feriados — pode haver pequena diferença
            frente ao calendário real da B3. Isto é uma ferramenta de simulação, não uma recomendação de investimento — confira sempre as
            condições reais junto à instituição financeira.
          </p>
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   LINHA DE INVESTIMENTO (formulário editável)
   ========================================================================= */
function InvestimentoRow({ inv, onUpdate, onRemove }) {
  const cfg = TIPOS[inv.tipo];
  let taxaLabel = "Taxa (% a.a.)";
  if (cfg.indexador === "CDI") taxaLabel = "% do CDI";
  if (cfg.indexador === "SELIC") taxaLabel = "% da Selic";
  if (cfg.indexador === "IPCA") taxaLabel = "Taxa prefixada — parte fixa acima da inflação (% a.a.)";
  if (cfg.indexador === "PRE") taxaLabel = "Taxa prefixada (% a.a.)";
  const showTaxaInput = cfg.indexador !== "POUP" && !cfg.taxaFixa;

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: '8px', padding: "16px 18px", marginBottom: 12, background: "#fafafa" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <input
          value={inv.nome}
          onChange={e => onUpdate(inv.id, "nome", e.target.value)}
          style={{ fontSize: 15.5, fontWeight: 700, background: "transparent", border: "none", borderBottom: "1px solid transparent", color: C.textDark, padding: "2px 0", flex: 1, minWidth: 0, outline: "none" }}
        />
        <button onClick={() => onRemove(inv.id)} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: `1px solid ${C.borderStrong}`, color: '#1C2D38', fontSize: 11.5, fontWeight: 600, padding: "7px 12px", borderRadius: 7, cursor: "pointer" }}>
          <FaTrash color="#d60101" size={13} /> Remover
        </button>
      </div>

      {(cfg.isento || cfg.indexador === "IPCA" || cfg.taxaFixa) && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, overflowX: "auto" }}>
          {cfg.isento && <Badge>Isento de IR</Badge>}
          {cfg.indexador === "IPCA" && <Badge gold>Híbrida: prefixado + IPCA</Badge>}
          {cfg.taxaFixa && <Badge gold>100% Selic</Badge>}
        </div>
      )}

      <Grid cols={4}>
        <Field label="Tipo">
          <select style={inputStyle} value={inv.tipo} onChange={e => onUpdate(inv.id, "tipo", e.target.value)}>
            {Object.entries(TIPOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Field>
        {showTaxaInput ? (
          <Field label={taxaLabel}>
            <input style={inputStyle} type="number" step="0.01" value={inv.taxa} onChange={e => onUpdate(inv.id, "taxa", e.target.value)} />
          </Field>
        ) : cfg.taxaFixa ? (
          <Field label="Indexador"><InfoBox>100% Selic</InfoBox></Field>
        ) : (
          <Field label="Regra"><InfoBox>Automática (Bacen)</InfoBox></Field>
        )}
        <Field label="Taxa adm. (% a.a., opcional)">
          <input style={inputStyle} type="number" step="0.01" value={inv.admFee || 0} onChange={e => onUpdate(inv.id, "admFee", e.target.value)} />
        </Field>
        <Field label="Custódia B3">
          <InfoBox>{cfg.custodia ? (cfg.isencao10k ? "0,20% a.a. só acima de R$10 mil" : "0,20% a.a. sobre o total") : "Não se aplica"}</InfoBox>
        </Field>
      </Grid>
    </div>
  );
}

/* =========================================================================
   PEQUENOS COMPONENTES UTILITÁRIOS
   ========================================================================= */
function Card({ children }) {
  return <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "20px 22px", marginBottom: 20 }}>{children}</div>;
}
function SectionLabel({ children }) {
  return <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: '#1C2D38', margin: "0 0 14px" }}>{children}</p>;
}
function Grid({ cols, children, style }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16, ...style }}>
      {children}
    </div>
  );
}
function Badge({ children, gold }) {
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 700, letterSpacing: "0.02em", borderRadius: 20, padding: "3px 10px", whiteSpace: "nowrap", flexShrink: 0,
      color: gold ? C.accentDark : C.blueMid, background: gold ? C.accentSoft : C.blueSoft,
    }}>{children}</span>
  );
}
function ResumoCard({ label, value, caption, highlight }) {
  return (
    <div style={{ background: highlight ? C.accentSoft : C.surface, border: `1px solid ${highlight ? C.accent : C.border}`, borderRadius: 10, padding: "16px 18px" }}>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: C.textMuted, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 21, fontWeight: 800, lineHeight: 1.1, color: highlight ? C.accentDark : C.textDark }}>{value}</div>
      <div style={{ fontSize: 11, color: C.textFaint, marginTop: 5 }}>{caption}</div>
    </div>
  );
}
function Td({ children, first, bold, neg, winner }) {
  return (
    <td style={{
      padding: "12px 8px", borderBottom: `1px solid ${C.border}`, textAlign: first ? "left" : "right",
      fontWeight: first ? 600 : bold ? 700 : 500, color: neg ? C.danger : winner ? C.accentDark : first ? C.textDark : C.textBody,
    }}>{children}</td>
  );
}
